import { KeyboardEvent } from "react";
import cn from "classnames";
import { RegisteredListItem } from "./use-section-navigation-utils";

export interface buildGetTogglePropsArgs {
  registeredLists: React.MutableRefObject<RegisteredListItem[]>;
  openedLists: (string | number)[];
  toggleOpenList: (item: string | number) => void;
  getBlocksWith: (props: any) => string;
  toggleCollapsedLabel?: string;
  toggleExpandedLabel?: string;
  uniqueName?: string;
  index: number | string;
  id: string;
  onClick: (onClickArgs: any) => void;
  onKeyDown: (onKeyDownArgs: any) => void;
  className?: string;
}

const spreadableProp = (prop: any | any[]) =>
  Array.isArray(prop) ? prop : [prop];

/**
 * buildGetToggleProps
 *
 * @param {string} id
 * @param {number | string} index
 * @param {Function} onClick
 * @param {Function} onKeyDown
 * @param {string | string[]} className
 */
const buildGetToggleProps: (args: buildGetTogglePropsArgs) => any = ({
  registeredLists,
  openedLists,
  toggleOpenList,
  getBlocksWith,
  toggleCollapsedLabel,
  toggleExpandedLabel,
  uniqueName,
  index,
  id: itemId,
  onClick: consumerOnClick = () => {},
  onKeyDown: consumerOnKeyDown = () => {},
  className: consumerClassName,
  ...consumerProps
}) => {
  /**
   * getParentContainerByIndex
   *
   * The index lookup needs to happen after the parent container has been registered.
   * The parent containers are registered inside of a ref callback which has its own lifecycle.
   * Offloading the lookup to a callback should ensure the component has had
   * enough time to initialize and the desired container is available.
   *
   * @param {string} containerIndex
   * @returns { index: number | string; ref: ReactNode }
   */
  const getParentContainerByIndex = (containerIndex: number | string) => {
    const listContainer = registeredLists.current.find(
      ({ index: listIndex }) => listIndex === containerIndex,
    );

    if (!listContainer) {
      return {};
    }

    return listContainer;
  };

  const isOpen = openedLists.indexOf(index) !== -1;

  return {
    onClick: (event: any) => {
      event.persist();
      toggleOpenList(index);
      consumerOnClick({
        event,
        containerProps: { ...getParentContainerByIndex(index) },
      });
    },
    onKeyDown: (event: KeyboardEvent) => {
      const { key } = event;
      event.persist();

      let keyHandled = false;

      switch (key) {
        case " ":
        case "Spacebar":
        case "Enter":
          keyHandled = true;
          break;
        case "Esc":
        case "Escape":
          if (isOpen) {
            keyHandled = true;
          }
      }

      if (keyHandled) {
        event.preventDefault();
        toggleOpenList(index);
      }

      consumerOnKeyDown({
        event,
        containerProps: { ...getParentContainerByIndex(index) },
      });
    },
    className: cn(
      getBlocksWith("__toggle"),
      isOpen && getBlocksWith("__toggle--open"),
      ...spreadableProp(consumerClassName),
    ),
    "aria-describedby": `${uniqueName}-${itemId}-label`,
    "aria-label": isOpen ? toggleExpandedLabel : toggleCollapsedLabel,
    "aria-expanded": isOpen,
    "aria-controls": `${uniqueName}-${index}`,
    ...consumerProps,
  };
};

export default buildGetToggleProps;
